// src/pages/Services/DigitalMarketing.jsx
import React, { useEffect } from 'react';
import './DigitalMarketing.css';


const DigitalMarketing = () => {


  useEffect(() => {
    // Hide nav on scroll down, show on scroll up
    let lastScrollTop = 0;
    const header = document.querySelector('.navbar');
    const handleScroll = () => {
      const y = window.pageYOffset || document.documentElement.scrollTop;
      header.style.top = (y > lastScrollTop && y > 100) ? '-100px' : '0';
      lastScrollTop = y <= 0 ? 0 : y;
    };
    window.addEventListener('scroll', handleScroll);

    // Intersection reveal for step cards & other elements
    const io = new IntersectionObserver((entries, obs) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in');
          obs.unobserve(entry.target);
        }
      });
    }, { threshold: 0.22 });

    document.querySelectorAll('.dm-reveal').forEach(el => io.observe(el));

    // Gentle pulse on primary button
    const btn = document.querySelector('.dm-btn-primary');
    if (btn) {
      let up = true;
      const interval = setInterval(() => {
        btn.style.boxShadow = up
          ? '0 12px 44px rgba(0,234,255,0.16)'
          : '0 8px 28px rgba(255,110,199,0.08)';
        up = !up;
      }, 2400);

      return () => clearInterval(interval);
    }

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      
      <section className="dm-hero">
        <div className="dm-hero-inner">
          <h1>Digital Marketing</h1>
          <h2 className="dm-glow-title">Transform Your Digital Footprint<br /><span>into Measurable Growth</span></h2>
          <p className="dm-hero-sub">Precision marketing. Real-time insights. Impact that compounds.</p>
          <a className="dm-btn-primary" href="#dm-cta">Book Your Free Strategy Session</a>
        </div>
        <div className="dm-hero-orb" aria-hidden="true"></div>
      </section>

      <section className="dm-vision" id="dm-vision">
        <div className="dm-vision-head">
          <h2 className="dm-vision-title">The Vision</h2>
          <p className="dm-vision-lead">Empower your brand with precision digital marketing that maximizes reach, conversions, and engagement.</p>
        </div>
        <div className="dm-vision-grid">
          <div className="dm-vision-card dm-float-up">
            <svg className="dm-neon-icon" viewBox="0 0 64 64" aria-hidden="true">
              <defs>
                <filter id="dm-glow1"><feGaussianBlur stdDeviation="2.5" result="coloredBlur"/><feMerge><feMergeNode in="coloredBlur"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
              </defs>
              <circle cx="32" cy="32" r="18" className="dm-s1"/>
              <circle cx="32" cy="32" r="12" className="dm-s2"/>
              <circle cx="32" cy="32" r="6" className="dm-s3"/>
              <path className="dm-s4" d="M32 2v10M62 32H52M32 62V52M2 32h10"/>
            </svg>
            <h3>Precision Targeting</h3>
            <p>Connect your message to the people most likely to act.</p>
          </div>
          <div className="dm-vision-card dm-float-up">
            <svg className="dm-neon-icon" viewBox="0 0 64 64" aria-hidden="true">
              <defs>
                <filter id="dm-glow2"><feGaussianBlur stdDeviation="2.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
              </defs>
              <circle cx="16" cy="16" r="6" className="dm-s3"/>
              <circle cx="48" cy="16" r="6" className="dm-s2"/>
              <circle cx="16" cy="48" r="6" className="dm-s1"/>
              <circle cx="48" cy="48" r="6" className="dm-s4"/>
              <path className="dm-s-line" d="M22 16h20M16 22v20M48 22v20M22 48h20"/>
            </svg>
            <h3>Omni-Channel Flow</h3>
            <p>Unified experiences across search, social, email, and web.</p>
          </div>
          <div className="dm-vision-card dm-float-up">
            <svg className="dm-neon-icon" viewBox="0 0 64 64" aria-hidden="true">
              <defs>
                <filter id="dm-glow3"><feGaussianBlur stdDeviation="2.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
              </defs>
              <polyline className="dm-pulse" points="4,34 16,34 22,18 30,46 38,28 44,34 60,34"/>
            </svg>
            <h3>Insight-Driven</h3>
            <p>Act on real-time analytics to maximize return.</p>
          </div>
        </div>
      </section>

      <section className="dm-benefits" id="dm-benefits">
        <div className="dm-benefits-head">
          <h2 className="dm-benefits-title">Benefits</h2>
          <p className="dm-benefits-lead">Outcomes you can measure—and scale.</p>
        </div>
        <div className="dm-benefit-grid">
          <article className="dm-benefit-card dm-pop">
            <div className="dm-badge">01</div>
            <h3>Global Visibility</h3>
            <p>Expand your brand’s presence to audiences worldwide.</p>
          </article>
          <article className="dm-benefit-card dm-pop">
            <div className="dm-badge">02</div>
            <h3>Tailored Targeting</h3>
            <p>Reach the right users with persona-driven segmentation.</p>
          </article>
          <article className="dm-benefit-card dm-pop">
            <div className="dm-badge">03</div>
            <h3>Efficient <br/> ROI</h3>
            <p>Run cost-effective campaigns that prioritize impact.</p>
          </article>
          <article className="dm-benefit-card dm-pop">
            <div className="dm-badge">04</div>
            <h3>Live Optimization</h3>
            <p>Adapt fast with real-time performance insights.</p>
          </article>
        </div>
      </section>

      <section className="dm-how" id="dm-how">
        <div className="dm-how-head">
          <h2 className="dm-how-title">How We Do It</h2>
          <p className="dm-how-lead">Independent steps. Clear ownership. Continuous lift.</p>
        </div>
        <div className="dm-steps">
          <div className="dm-step-card dm-reveal">
            <div className="dm-step-icon">
              <svg viewBox="0 0 64 64" aria-hidden="true">
                <defs><filter id="dm-gA"><feGaussianBlur stdDeviation="2.2" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter></defs>
                <circle cx="32" cy="32" r="20" className="dm-s1"/>
                <path d="M32 12a20 20 0 0 1 0 40" className="dm-arc"/>
                <circle cx="42" cy="22" r="3" className="dm-dot"/>
                <line x1="32" y1="32" x2="48" y2="16" className="dm-s-line"/>
              </svg>
            </div>
            <h3>Discover & Analyze</h3>
            <p>Understand your market, audience, and competition to pinpoint leverage.</p>
          </div>
          <div className="dm-step-card dm-reveal">
            <div className="dm-step-icon">
              <svg viewBox="0 0 64 64" aria-hidden="true">
                <defs><filter id="dm-gB"><feGaussianBlur stdDeviation="2.2" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter></defs>
                <rect x="10" y="10" width="44" height="44" rx="4" className="dm-s2"/>
                <path d="M18 20h28M18 28h20M18 36h24M18 44h16" className="dm-grid"/>
              </svg>
            </div>
            <h3>Strategize & Design</h3>
            <p>Create a unique multi-channel plan with creative built to stand out.</p>
          </div>
          <div className="dm-step-card dm-reveal">
            <div className="dm-step-icon">
              <svg viewBox="0 0 64 64" aria-hidden="true">
                <defs><filter id="dm-gC"><feGaussianBlur stdDeviation="2.2" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter></defs>
                <path className="dm-rocket" d="M36 6c10 2 16 8 18 18-6 0-12 2-18 8-6-6-8-12-8-18 6-2 8-2 8-8z"/>
                <circle cx="40" cy="18" r="4" className="dm-window"/>
                <path className="dm-flame" d="M20 44c2 6 8 10 12 12-2-6-2-12 0-18-6 2-10 8-12 6z"/>
              </svg>
            </div>
            <h3>Execute & Optimize</h3>
            <p>Launch fast, monitor continuously, and refine in real time.</p>
          </div>
          <div className="dm-step-card dm-reveal">
            <div className="dm-step-icon">
              <svg viewBox="0 0 64 64" aria-hidden="true">
                <defs><filter id="dm-gD"><feGaussianBlur stdDeviation="2.2" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter></defs>
                <path className="dm-loop" d="M14 34a18 18 0 0 0 32 10"/>
                <path className="dm-loop" d="M50 30A18 18 0 0 0 18 20"/>
                <polyline className="dm-arrow" points="42,42 46,44 48,40"/>
                <polyline className="dm-arrow" points="20,22 16,20 14,24"/>
              </svg>
            </div>
            <h3>Review & Iterate</h3>
            <p>Analyze outcomes and evolve the playbook for compounding gains.</p>
          </div>
        </div>
      </section>

      <section className="dm-why" id="dm-why">
        <div className="dm-why-head">
          <h2 className="dm-why-title">Why Choose Us</h2>
          <p className="dm-why-lead">Technology-first marketing. Transparent execution.</p>
        </div>
        <ul className="dm-why-list">
          <li>
            <span className="dm-check">✓</span>
            A certified IT services firm delivering tech-savvy marketing solutions.
          </li>
          <li>
            <span className="dm-check">✓</span>
            Transparent, collaborative process tailored to your team.
          </li>
          <li>
            <span className="dm-check">✓</span>
            Reach beyond borders while maintaining local relevance.
          </li>
        </ul>
      </section>

      <section className="dm-cta" id="dm-cta">
        <div className="dm-cta-card">
          <h3>We’ll handle your digital growth</h3>
          <p>Start with a free strategy session today.</p>
          <a className="dm-btn-primary" href="mailto:info@oakyaitservices.com">info@oakyaitservices.com</a>
        </div>
      </section>

      
    </>
  );
};

export default DigitalMarketing;